/* __next_internal_client_entry_do_not_use__ Radio,Label,default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { Typography } from "@faire/web--source/slate/Typography";
import { useUniqueId } from "@faire/web--source/ui/hooks/useUniqueId";
import * as React from "react";
import styled, { css } from "styled-components";
import { themeOrDefault } from "../Theme";
import { HIGH_VIS_FILTER, highVisSlateComponent } from "../utils/highVisSlateComponent";
var RADIO_DIAMETER = 16;
/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/radio)
 */ export var Radio = function(props) {
    var _props_active = props.active, active = _props_active === void 0 ? false : _props_active, align = props.align, children = props.children, className = props.className, color = props.color, _props_disabled = props.disabled, disabled = _props_disabled === void 0 ? false : _props_disabled, disabledColor = props.disabledColor, _props_error = props.error, error = _props_error === void 0 ? false : _props_error, label = props.label, name = props.name, onClick = props.onClick, secondaryLabel = props.secondaryLabel;
    var labelId = useUniqueId("radio-button-label-");
    return /*#__PURE__*/ _jsxs(RadioWrapper, {
        $align: align,
        className: className,
        children: [
            /*#__PURE__*/ _jsx(RadioInnerWrapper, {
                children: /*#__PURE__*/ _jsx(RadioInput, {
                    $active: active,
                    $align: align,
                    "aria-label": props["aria-label"],
                    "aria-labelledby": props["aria-labelledby"],
                    checked: active,
                    color: color,
                    "data-test-id": props["data-test-id"],
                    disabled: disabled,
                    $disabledColor: disabledColor,
                    $error: error,
                    id: labelId,
                    name: name,
                    onChange: onClick,
                    type: "radio",
                    readOnly: onClick === undefined
                })
            }),
            children || label ? /*#__PURE__*/ _jsx(Label, {
                disabled: disabled,
                htmlFor: labelId,
                children: children || /*#__PURE__*/ _jsxs(LabelText, {
                    align: "left",
                    color: color,
                    $disabled: disabled,
                    $disabledColor: disabledColor,
                    $error: error,
                    id: labelId,
                    children: [
                        label,
                        /*#__PURE__*/ _jsx(SecondaryLabel, {
                            children: secondaryLabel
                        })
                    ]
                })
            }) : null
        ]
    });
};
var SecondaryLabel = styled(Typography).attrs({
    color: Core.text.subdued,
    forwardedAs: "span",
    variant: "paragraphSansRegular"
}).withConfig({
    componentId: "sc-d2f2eaf2-0"
})([
    "display:block;"
]);
var RadioWrapper = styled.div.attrs({
    role: "presentation"
}).withConfig({
    componentId: "sc-d2f2eaf2-1"
})([
    "align-items:",
    ";display:flex;",
    ";"
], function(props) {
    var _props_$align;
    return (_props_$align = props.$align) !== null && _props_$align !== void 0 ? _props_$align : "center";
}, highVisSlateComponent(HIGH_VIS_FILTER.COMPONENT));
var RadioInnerWrapper = styled.div.withConfig({
    componentId: "sc-d2f2eaf2-2"
})([
    "align-items:center;display:flex;height:",
    ";"
], function(param) {
    var theme = param.theme;
    var _theme_fonts_sansSerif_p, _theme_fonts_sansSerif, _theme_fonts;
    return (theme === null || theme === void 0 ? void 0 : (_theme_fonts = theme.fonts) === null || _theme_fonts === void 0 ? void 0 : (_theme_fonts_sansSerif = _theme_fonts.sansSerif) === null || _theme_fonts_sansSerif === void 0 ? void 0 : (_theme_fonts_sansSerif_p = _theme_fonts_sansSerif.p) === null || _theme_fonts_sansSerif_p === void 0 ? void 0 : _theme_fonts_sansSerif_p.lineHeight) || "12px";
});
var RadioInput = styled.input.withConfig({
    componentId: "sc-d2f2eaf2-3"
})([
    "appearance:none;border:1px solid ",
    ";block-size:",
    "px;border-radius:50%;cursor:",
    ";inline-size:",
    "px;margin:0;min-block-size:",
    "px;min-inline-size:",
    "px;position:relative;background-color:",
    ";&:focus-visible{outline:revert;}",
    ";"
], function(param) {
    var color = param.color, disabledProp = param.disabled, disabledColor = param.$disabledColor, errorProp = param.$error, theme = param.theme;
    var _themeOrDefault = themeOrDefault(theme), enabled = _themeOrDefault.radios.enabled;
    if (disabledProp) {
        return disabledColor || Core.border.muted;
    } else if (errorProp) {
        return Core.border.critical;
    } else {
        return color !== null && color !== void 0 ? color : enabled;
    }
}, RADIO_DIAMETER, function(param) {
    var disabled = param.disabled;
    return disabled ? "not-allowed" : "pointer";
}, RADIO_DIAMETER, RADIO_DIAMETER, RADIO_DIAMETER, function(param) {
    var disabledProp = param.disabled, active = param.$active, disabledColor = param.$disabledColor;
    if (disabledProp && !active) {
        return disabledColor || Core.surface.subdued;
    } else {
        return;
    }
}, function(param) {
    var active = param.$active, color = param.color, disabled = param.disabled, disabledColor = param.$disabledColor, error = param.$error, theme = param.theme;
    var radios = themeOrDefault(theme).radios;
    var activeBackgroundColor;
    if (disabled) {
        activeBackgroundColor = disabledColor !== null && disabledColor !== void 0 ? disabledColor : radios.disabled;
    } else if (error) {
        activeBackgroundColor = Core.text.critical;
    } else {
        activeBackgroundColor = color !== null && color !== void 0 ? color : radios.enabled;
    }
    return active ? css([
        "&:before{background-color:",
        ";block-size:",
        'px;border-radius:50%;content:" ";inline-size:',
        "px;inset-block-start:3px;inset-inline-start:3px;position:absolute;}"
    ], activeBackgroundColor, RADIO_DIAMETER / 2, RADIO_DIAMETER / 2) : "";
});
export var Label = styled.label.withConfig({
    componentId: "sc-d2f2eaf2-4"
})([
    "cursor:",
    ";padding-inline-start:8px;"
], function(param) {
    var disabled = param.disabled;
    return disabled ? "not-allowed" : "pointer";
});
var LabelText = styled(Typography).withConfig({
    componentId: "sc-d2f2eaf2-5"
})([
    "&&&{color:",
    ";}"
], function(param) {
    var $disabled = param.$disabled, $error = param.$error, theme = param.theme, color = param.color, $disabledColor = param.$disabledColor;
    var radios = themeOrDefault(theme).radios;
    var labelTextColor;
    if ($disabled) {
        labelTextColor = $disabledColor || radios.disabled;
    } else if ($error) {
        labelTextColor = Core.text.critical;
    } else {
        labelTextColor = color || radios.enabled;
    }
    return labelTextColor;
});
export default Radio;
